import { Injectable, signal } from '@angular/core';
import { Patient } from '../models/patient.model';

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  private patients = signal<Patient[]>([]);

  constructor() {
    // Initialize with mock data
    this.patients.set([
      {
        id: '1',
        firstName: 'Alice',
        lastName: 'Johnson',
        email: 'alice@example.com',
        phone: '+1234567890',
        dateOfBirth: new Date('1990-05-15'),
        gender: 'Female',
        address: '123 Main St',
        city: 'New York',
        state: 'NY',
        zipCode: '10001',
        emergencyContact: {
          name: 'Bob Johnson',
          phone: '+1234567891',
          relationship: 'Spouse'
        },
        bloodGroup: 'O+',
        allergies: ['Penicillin'],
        medicalHistory: ['Hypertension'],
        insurance: {
          provider: 'Blue Cross',
          policyNumber: 'BC123456',
          groupNumber: 'GR001'
        },
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: '2',
        firstName: 'Bob',
        lastName: 'Smith',
        email: 'bob@example.com',
        phone: '+1234567892',
        dateOfBirth: new Date('1985-08-20'),
        gender: 'Male',
        address: '456 Oak Ave',
        city: 'Los Angeles',
        state: 'CA',
        zipCode: '90001',
        emergencyContact: {
          name: 'Jane Smith',
          phone: '+1234567893',
          relationship: 'Wife'
        },
        bloodGroup: 'A+',
        allergies: [],
        medicalHistory: ['Diabetes'],
        insurance: {
          provider: 'Aetna',
          policyNumber: 'AE789012',
        },
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ]);
  }

  getPatients() {
    return this.patients.asReadonly();
  }

  getPatientById(id: string): Patient | undefined {
    return this.patients().find(p => p.id === id);
  }

  addPatient(patient: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>): Patient {
    const newPatient: Patient = {
      ...patient,
      id: Date.now().toString(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    this.patients.update(patients => [...patients, newPatient]);
    return newPatient;
  }

  updatePatient(id: string, updates: Partial<Patient>): void {
    this.patients.update(patients =>
      patients.map(p =>
        p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p
      )
    );
  }

  deletePatient(id: string): void {
    this.patients.update(patients => patients.filter(p => p.id !== id));
  }

  searchPatients(query: string): Patient[] {
    const lowerQuery = query.toLowerCase();
    return this.patients().filter(p =>
      p.firstName.toLowerCase().includes(lowerQuery) ||
      p.lastName.toLowerCase().includes(lowerQuery) ||
      p.email.toLowerCase().includes(lowerQuery) ||
      p.phone.includes(query)
    );
  }
}

